import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { T, US_STATES, familyColor } from '../tokens';

// ─── Icons ────────────────────────────────────────────────────────────────────

const ArrowLeft = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalendarIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 10h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PinIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <path d="M12 22s7-7 7-12a7 7 0 10-14 0c0 5 7 12 7 12z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const UsersIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <circle cx="9" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2 21c1-4 3.5-6 7-6s6 2 7 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M19 11c1.5 0 2.5 1 2.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="17.5" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const ClockIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ButterflyIcon = ({ size = 14, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <path d="M 19.6604 10.6914 L 11.4803 4.7485 c -1.7632 -1.2812 -3.8792 -1.5234 -5.6996 -0.2011 C 5.4596 4.7814 5.088 5.105 4.821 5.4723 c -1.9839 2.7305 0.1707 11.2487 2.9011 13.2324 c 1.6467 1.1966 3.4695 1.5935 5.677 1.2443 c -0.9448 0.2741 -1.7554 0.7642 -2.4349 1.471 c -2.4324 2.513 -3.3402 4.8042 -2.2317 6.3291 C 9.8087 29.2298 11.2848 31.0271 12.1949 31.6885 c 2.7305 1.9839 7.5469 -4.0056 7.4652 -7.1253 L 19.6604 10.6914 z m 2.5457 0 L 30.386 4.7487 C 32.1497 3.4672 34.2655 3.2254 36.0857 4.5476 c 0.3221 0.234 0.6935 0.5575 0.9605 0.9244 c 1.9833 2.7308 -0.171 11.2488 -2.9019 13.2324 c -1.6467 1.197 -3.4691 1.5939 -5.6763 1.2444 c 0.9438 0.2737 1.7554 0.7646 2.4344 1.4715 c 2.4327 2.5133 3.3402 4.8037 2.2323 6.3288 c -1.0762 1.4811 -2.552 3.2781 -3.4625 3.9393 c -2.7302 1.9839 -7.5474 -4.005 -7.465 -7.1256 l -0.0006 -13.8715 z" 
      stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);


const TrashIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Types matching the actual backend response ───────────────────────────────

interface ButterflyEntity {
  id: number;
  commonName: string;
  scientificName: string;
  commonFamilyName: string;
  scientificFamilyName: string;
}

interface SurveyDetailId {
  surveyId: number;
  speciesId: number;
}

interface SurveyDetail {
  id: SurveyDetailId;
  butterfly: ButterflyEntity;
  sectionSightings: string[] | null;
  total: number;
  notes: string | null;
}

interface Survey {
  id: number;
  name: string;
  type: string;
  description: string;
  location: string;
  numRecorders: number;
  recorderNames: string[];
  sections: string[];
  windSpeed: number;
  cloudCoverage: number;
  temperature: number;
  surveyDate: string;
  startTime: number | null;
  endTime: number | null;
  secondsElapsed: number | null;
  submittedAt: string | null;
  comments: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stateName(code: string) {
  const found = US_STATES.find(([c]) => c === code);
  return found ? found[1] : code;
}

function formatDate(seconds: number | null | undefined) {
  if (!seconds) return '—';
  const d = new Date(seconds * 1000);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDuration(seconds: number | null | undefined) {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

function typeLabel(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('pollard')) return 'Pollard';
  if (t.includes('meander')) return 'Meandering';
  if (t.includes('incident')) return 'Incidental';
  return type || 'Survey';
}

function typeColor(type: string) {
  const t = (type || '').toLowerCase();
  if (t.includes('pollard')) return { bg: T.forestSoft, color: T.forest };
  if (t.includes('meander')) return { bg: T.honeySoft, color: '#9A6018' };
  if (t.includes('incident')) return { bg: '#EEE8F4', color: '#6B4C8A' };
  return { bg: T.bgDeep, color: T.ink2 };
}

// A survey is fully submitted only if secondsElapsed was recorded (set by submitAll).
// submittedAt is always populated by @PrePersist so it can't be used to distinguish drafts.
function isSubmitted(survey: Survey) {
  return survey.secondsElapsed != null && survey.secondsElapsed > 0;
}

// ─── Meta chip ────────────────────────────────────────────────────────────────

function MetaChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: T.font, fontSize: 12, color: T.ink2 }}>
      <span style={{ color: T.ink3 }}>{icon}</span>
      {label}
    </span>
  );
}

// ─── Survey card ──────────────────────────────────────────────────────────────

function SurveyCard({ survey, onDelete }: { survey: Survey; onDelete: (id: number) => void }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState<SurveyDetail[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const submitted = isSubmitted(survey);
  const tc = typeColor(survey.type);
  const duration = formatDuration(survey.secondsElapsed);

  async function loadDetails() {
    if (details.length > 0) return;
    setLoadingDetails(true);
    try {
      const res = await fetch(`/api/surveys/${survey.id}/details`);
      if (res.ok) setDetails(await res.json());
    } catch {
      // details stay empty
    } finally {
      setLoadingDetails(false);
    }
  }

  function handleToggle() {
    if (!open) loadDetails();
    setOpen(o => !o);
  }

  async function handleDelete() {
    try {
      const res = await fetch(`/api/surveys/${survey.id}`, { method: 'DELETE' });
      if (res.ok) onDelete(survey.id);
    } catch {
      // ignore
    }
    setConfirmDelete(false);
  }

  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.line}`,
      borderRadius: T.r.lg,
      overflow: 'hidden',
      boxShadow: T.shadowCard,
    }}>
      <button onClick={handleToggle} style={{
        width: '100%', textAlign: 'left', background: 'none',
        border: 'none', cursor: 'pointer', padding: '16px 16px 14px',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{
                fontFamily: T.font, fontSize: 11, fontWeight: 600,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                background: tc.bg, color: tc.color,
                padding: '2px 8px', borderRadius: T.r.pill,
              }}>
                {typeLabel(survey.type)}
              </span>
              <span style={{
                fontFamily: T.font, fontSize: 11, fontWeight: 600,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                background: submitted ? '#E5F4E8' : '#FFF7E6',
                color: submitted ? '#2A7A35' : '#A07010',
                padding: '2px 8px', borderRadius: T.r.pill,
              }}>
                {submitted ? 'Submitted' : 'Draft'}
              </span>
            </div>

            <div style={{
              fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 600,
              color: T.ink, letterSpacing: '-0.01em',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {survey.name || 'Unnamed Survey'}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 8 }}>
              <MetaChip icon={<CalendarIcon />} label={formatDate(survey.startTime)} />
              {survey.location && <MetaChip icon={<PinIcon />} label={stateName(survey.location)} />}
              {survey.numRecorders > 0 && (
                <MetaChip icon={<UsersIcon />} label={`${survey.numRecorders} recorder${survey.numRecorders !== 1 ? 's' : ''}`} />
              )}
              {duration && <MetaChip icon={<ClockIcon />} label={duration} />}
            </div>
          </div>

          <span style={{ color: T.ink3, marginTop: 2, flexShrink: 0 }}>
            <ChevronDown open={open} />
          </span>
        </div>
      </button>

      {open && (
        <div style={{ borderTop: `1px solid ${T.line}`, padding: '14px 16px 16px' }}>
          {survey.description ? (
            <div style={{ fontFamily: T.font, fontSize: 13, color: T.ink2, marginBottom: 14, lineHeight: 1.5 }}>
              {survey.description}
            </div>
          ) : null}

          <div style={{
            fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.ink3,
            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8,
          }}>
            Butterfly sightings
          </div>

          {loadingDetails ? (
            <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink3, padding: '8px 0' }}>Loading…</div>
          ) : details.length === 0 ? (
            <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink3, padding: '12px 0', textAlign: 'center' }}>
              No sightings recorded
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {details
                .slice()
                .sort((a, b) => b.total - a.total)
                .map(d => {
                  const name = d.butterfly?.commonName ?? '—';
                  const sci = d.butterfly?.scientificName ?? '';
                  const family = d.butterfly?.commonFamilyName ?? d.butterfly?.scientificFamilyName ?? '';
                  const key = d.id?.speciesId ?? d.butterfly?.id;
                  return (
                    <div key={key} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 10px', borderRadius: T.r.sm, background: T.bg,
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: T.r.sm, flexShrink: 0,
                        background: familyColor(family),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <ButterflyIcon size={16} color="#fff" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: T.font, fontSize: 14, fontWeight: 600, color: T.ink,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {name}
                        </div>
                        {sci ? (
                          <div style={{ fontFamily: T.fontMono, fontSize: 11, color: T.ink3, fontStyle: 'italic' }}>
                            {sci}
                          </div>
                        ) : null}
                      </div>
                      <div style={{
                        fontFamily: T.fontDisplay, fontSize: 18, fontWeight: 700,
                        color: T.forest, letterSpacing: '-0.02em', flexShrink: 0,
                      }}>
                        {d.total}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {submitted ? (
              <button
                onClick={() => {
                  localStorage.setItem('surveyId', survey.id.toString());
                  navigate('/summary');
                }}
                style={{
                  fontFamily: T.font, fontSize: 13, fontWeight: 500, color: T.forest,
                  background: 'none', border: `1px solid ${T.forest}20`,
                  borderRadius: T.r.sm, padding: '5px 10px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                Summary
              </button>
              ) : (null)
            }

            {confirmDelete ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontFamily: T.font, fontSize: 13, color: T.ink2 }}>Delete this survey?</span>
                <button
                  onClick={() => setConfirmDelete(false)}
                  style={{
                    fontFamily: T.font, fontSize: 13, fontWeight: 500, color: T.ink2,
                    background: T.bg, border: `1px solid ${T.line}`, borderRadius: T.r.sm,
                    padding: '5px 12px', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  style={{
                    fontFamily: T.font, fontSize: 13, fontWeight: 600, color: '#fff',
                    background: T.rust, border: 'none', borderRadius: T.r.sm,
                    padding: '5px 12px', cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                style={{
                  fontFamily: T.font, fontSize: 13, fontWeight: 500, color: T.rust,
                  background: 'none', border: `1px solid ${T.rust}20`,
                  borderRadius: T.r.sm, padding: '5px 10px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                <TrashIcon /> Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type Filter = 'all' | 'submitted' | 'draft';

export default function RecentSurveys() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  // Auth response stores userId (not id) — confirmed by SurveyRecordingComponent line 254
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userId: number | undefined = user?.userId;

  useEffect(() => {
    if (!userId) { setLoading(false); setError('Not logged in'); return; }
    fetch(`/api/surveys/user/${userId}`)
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((data: Survey[]) => {
        // Sort newest survey date first
        const sorted = data.slice().sort((a, b) => {
          const da = a.surveyDate ? new Date(a.surveyDate).getTime() : 0;
          const db = b.surveyDate ? new Date(b.surveyDate).getTime() : 0;
          return db - da;
        });
        setSurveys(sorted);
      })
      .catch(e => setError(`Could not load surveys (${e.message})`))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    if(localStorage.getItem("user") == null) {
      localStorage.clear();
      navigate("/");
    }
  }, [])

  function handleDelete(id: number) {
    setSurveys(s => s.filter(sv => sv.id !== id));
  }

  const submittedCount = surveys.filter(isSubmitted).length;
  const draftCount = surveys.filter(s => !isSubmitted(s)).length;

  const visible = surveys.filter(s => {
    if (filter === 'submitted' && !isSubmitted(s)) return false;
    if (filter === 'draft' && isSubmitted(s)) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (
        (s.name || '').toLowerCase().includes(q) ||
        (s.location || '').toLowerCase().includes(q) ||
        stateName(s.location).toLowerCase().includes(q) ||
        (s.type || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="lep-page">
      <div className="lep-page-inner" style={{ padding: '0 0 48px' }}>

        {/* Sticky header */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 10,
          background: T.bg, borderBottom: `1px solid ${T.line}`,
          padding: '54px 20px 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <button
              onClick={() => navigate('/menu')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: T.ink2, padding: 4, margin: -4,
                display: 'flex', alignItems: 'center',
              }}
            >
              <ArrowLeft />
            </button>
            <div style={{
              fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 600,
              color: T.ink, letterSpacing: '-0.02em', flex: 1,
            }}>
              Recent Surveys
            </div>
            {!loading && surveys.length > 0 && (
              <div style={{ fontFamily: T.font, fontSize: 13, color: T.ink3 }}>
                {surveys.length} total
              </div>
            )}
          </div>

          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, location, or type…"
              style={{
                width: '100%', boxSizing: 'border-box',
                height: 40, paddingLeft: 36, paddingRight: 12,
                fontFamily: T.font, fontSize: 15, color: T.ink,
                background: T.bgDeep, border: `1px solid ${T.line}`,
                borderRadius: T.r.md, outline: 'none',
              }}
            />
            <span style={{
              position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
              color: T.ink3, pointerEvents: 'none',
            }}>
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="M16.5 16.5l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </span>
          </div>

          {/* Filter tabs */}
          <div className="lep-seg" style={{ borderRadius: `${T.r.md}px ${T.r.md}px 0 0` }}>
            {([
              ['all', 'All', surveys.length],
              ['submitted', 'Submitted', submittedCount],
              ['draft', 'Drafts', draftCount],
            ] as [Filter, string, number][]).map(([val, label, count]) => (
              <button
                key={val}
                className={`lep-seg-btn${filter === val ? ' active' : ''}`}
                onClick={() => setFilter(val)}
              >
                {label}
                <span style={{
                  marginLeft: 5, fontFamily: T.fontMono, fontSize: 11,
                  opacity: filter === val ? 0.6 : 0.4,
                }}>
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '48px 0', fontFamily: T.font, fontSize: 15, color: T.ink3 }}>
              Loading surveys…
            </div>
          )}

          {!loading && error && (
            <div style={{ textAlign: 'center', padding: '48px 20px', fontFamily: T.font, fontSize: 15, color: T.rust }}>
              {error}
            </div>
          )}

          {!loading && !error && visible.length === 0 && (
            <div style={{
              textAlign: 'center', padding: '64px 20px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
            }}>
              <div style={{ opacity: 0.25 }}>
                <ButterflyIcon size={48} color={T.ink} />
              </div>
              <div style={{
                fontFamily: T.fontDisplay, fontSize: 20, fontWeight: 600,
                color: T.ink, letterSpacing: '-0.02em',
              }}>
                {search
                  ? 'No matches'
                  : filter === 'draft'
                  ? 'No drafts'
                  : filter === 'submitted'
                  ? 'No submitted surveys'
                  : 'No surveys yet'}
              </div>
              <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink2 }}>
                {search ? 'Try a different search term' : 'Start a new survey from the main menu'}
              </div>
            </div>
          )}

          {!loading && !error && visible.map(survey => (
            <SurveyCard key={survey.id} survey={survey} onDelete={handleDelete} />
          ))}
        </div>
      </div>
    </div>
  );
}
